<script lang="ts">
	import { datadogRum } from '@datadog/browser-rum';
	import Title from '$lib/components/Title.svelte';
	import { m } from '$lib/paraglide/messages.js';

	datadogRum.setViewName('/discount-codes');

	let { store }: { store: App.PageData['store'] } = $props();

	let copiedStates: Record<string, boolean> = $state({});

	async function handleCopy(code: string) {
		await navigator.clipboard.writeText(code);

		copiedStates[code] = true;

		setTimeout(() => {
			copiedStates[code] = false;
		}, 2000);
	}
</script>

<div class="full-container mx-auto mt-10 px-4 xl:px-5">
	<div class="prose prose-cms-content">
		<Title class="font-extrabold">
			{m['discount_codes.title']({ storeName: store.name })}
		</Title>
		<p class="mt-4 max-w-2xl text-lg font-bold">
			{m['discount_codes.save_on_purchase']({ storeName: store.name })}
		</p>

		<div class="hidden xl:block">
			<p>{m['discount_codes.description']({ storeName: store.name })}</p>
			<p>{m['discount_codes.instruction']({ storeName: store.name })}</p>
		</div>

		<div class="not-prose mt-6 grid gap-6 lg:grid-cols-2 xl:grid-cols-3 xl:gap-8">
			{#each store.coupons as coupon (coupon.code)}
				<div
					class="relative flex flex-col items-stretch rounded-xl bg-neutral-100 shadow-md sm:flex-row"
				>
					<div
						class="absolute top-1/2 -left-4 hidden h-9 w-9 -translate-y-1/2 rounded-full bg-white inset-shadow-[-8px_0_8px_-8px_rgba(0,0,0,0.1)] sm:block"
					></div>
					<div
						class="absolute top-1/2 -right-4 hidden h-9 w-9 -translate-y-1/2 rounded-full bg-white inset-shadow-[8px_0_6px_-8px_rgba(0,0,0,0.1)] sm:block"
					></div>

					<div class="relative flex-grow p-4 sm:p-6">
						<div class="mb-1 text-lg font-bold wrap-anywhere text-neutral-800 sm:text-xl">
							{coupon.label}
						</div>
						{#if coupon.description}
							<div class="prose max-w-none text-base text-neutral-700">
								{@html coupon.description}
							</div>
						{/if}
						{#if coupon.legal_information}
							<p class="mt-3 text-xs text-neutral-500 italic">
								{@html coupon.legal_information}
							</p>
						{/if}
						<div
							class="absolute top-full -left-4 block h-9 w-9 -translate-y-1/2 rounded-full bg-white inset-shadow-[-8px_0_8px_-8px_rgba(0,0,0,0.1)] sm:hidden"
						></div>
						<div
							class="absolute top-full -right-4 block h-9 w-9 -translate-y-1/2 rounded-full bg-white inset-shadow-[8px_0_6px_-8px_rgba(0,0,0,0.1)] sm:hidden"
						></div>
					</div>

					<div
						class="flex w-full flex-shrink-0 flex-col items-center justify-center border-t-2 border-l-0 border-dashed border-neutral-300 bg-neutral-200/50 p-3 sm:w-48 sm:rounded-r-xl sm:border-t-0 sm:border-l-2 sm:p-4"
					>
						<div
							class="mb-3 text-center font-mono text-lg font-bold tracking-widest wrap-anywhere text-neutral-800 sm:mb-4 sm:text-xl"
						>
							{coupon.code}
						</div>
						<button
							class={[
								'w-full max-w-10/12 cursor-pointer rounded-lg py-2 text-sm font-semibold text-white transition-all duration-200 ease-in-out focus:ring-2 focus:ring-offset-2 focus:outline-none xl:py-3',
								copiedStates[coupon.code]
									? 'bg-green-600 focus:ring-green-400'
									: 'bg-neutral-800 hover:bg-neutral-700 focus:ring-neutral-500',
							]}
							onclick={() => handleCopy(coupon.code)}
							disabled={copiedStates[coupon.code]}
						>
							{#if copiedStates[coupon.code]}
								<span>{m['discount_codes.code_copied']()}</span>
							{:else}
								<span>{m['discount_codes.copy_code']()}</span>
							{/if}
						</button>
					</div>
				</div>
			{:else}
				<div
					class="col-span-full flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-neutral-300 bg-neutral-50 p-10 text-center"
				>
					<span class="icon-[mdi--ticket-confirmation-outline] w-16 h-16 text-neutral-400"></span>
					<h3 class="text-xl font-semibold text-neutral-800">
						{m['discount_codes.no_codes_title']()}
					</h3>
					<p class="mt-2 text-neutral-600">
						{m['discount_codes.no_codes_description']()}
					</p>
				</div>
			{/each}
		</div>

		<div class="mt-8 max-w-none">
			<div class="block xl:hidden">
				<p>{m['discount_codes.description']({ storeName: store.name })}</p>
				<p>{m['discount_codes.instruction']({ storeName: store.name })}</p>
			</div>
			<p class="xl:text-xl xl:font-bold">{m['discount_codes.how_to_use']()}</p>
			<p>{m['discount_codes.exclusions']()}</p>
			<p>{m['discount_codes.happy_shopping']()}</p>
		</div>
	</div>
</div>
